import { storableError } from '../../util/errors';
import { createImageVariantConfig } from '../../util/sdkLoader';
import { addMarketplaceEntities, getListingsById } from '../../ducks/marketplaceData.duck';
import { withBusyCursor } from '../../util/busyCursor';
import { isArrayLength } from '../../util/genericHelpers';
import {
  fetchCurrentUser,
  fetchCurrentUserCartCount,
  saveCurrentUserProfile,
  selectWishlistIds,
  toggleWishlistItem,
} from '../../ducks/user.duck';
import { getUserCartDetails, getUserCartQuantities } from '../../util/dataExtractors';
import {
  getLocalCartItems,
  setLocalCartItems,
  getLocalCartQuantities,
  setLocalCartQuantities,
} from '../../util/guestStorage';
import { queryListingsByIds } from '../../util/listingsByIds';
import { cartShopsOf, cartSummaryOf } from './cartHelpers';

// ================ Action types ================ //

export const FETCH_LISTING_REQUEST = 'app/CartPage/FETCH_LISTING_REQUEST';
export const FETCH_LISTING_SUCCESS = 'app/CartPage/FETCH_LISTING_SUCCESS';
export const FETCH_LISTING_ERROR = 'app/CartPage/FETCH_LISTING_ERROR';

export const CART_QUANTITIES_LOADED = 'app/CartPage/CART_QUANTITIES_LOADED';
export const CART_QUANTITY_CHANGED = 'app/CartPage/CART_QUANTITY_CHANGED';
export const CART_QUANTITY_SAVE_ERROR = 'app/CartPage/CART_QUANTITY_SAVE_ERROR';

export const ADD_ITEM_TO_CART_REQUEST = 'app/CartPage/ADD_ITEM_TO_CART_REQUEST';
export const ADD_ITEM_TO_CART_DONE = 'app/CartPage/ADD_ITEM_TO_CART_DONE';
export const ADD_ITEM_TO_CART_ERROR = 'app/CartPage/ADD_ITEM_TO_CART_ERROR';

export const REMOVE_ITEM_FROM_CART_REQUEST = 'app/CartPage/REMOVE_ITEM_FROM_CART_REQUEST';
export const REMOVE_ITEM_FROM_CART_SUCCESS = 'app/CartPage/REMOVE_ITEM_FROM_CART_SUCCESS';
export const REMOVE_ITEM_FROM_CART_ERROR = 'app/CartPage/REMOVE_ITEM_FROM_CART_ERROR';

export const SAVE_FOR_LATER_REQUEST = 'app/CartPage/SAVE_FOR_LATER_REQUEST';
export const SAVE_FOR_LATER_DONE = 'app/CartPage/SAVE_FOR_LATER_DONE';
export const SAVE_FOR_LATER_ERROR = 'app/CartPage/SAVE_FOR_LATER_ERROR';

// ================ Reducer ================ //

const initialState = {
  currentPageResultIds: [],
  fetchListingInProgress: false,
  fetchListingError: null,
  // The latest listings request; an older answer is dropped.
  fetchListingRequest: null,
  // How many of each listing the page shows ({ listingId: quantity }). Set from
  // the saved cart as the page loads and changed at once on "+" and "-".
  quantities: {},
  // Listing ids being added from a listing page, or removed from the cart.
  addItemToCartInProgress: [],
  removeItemFromCartInProgress: [],
  // Listing ids being moved to the buyer's favorites ("Save for later").
  saveForLaterInProgress: [],
  // The last cart change that wasn't saved: { id, error }.
  cartSaveError: null,
};

const resultIds = data => data.data.map(l => l.id);
const without = (list, id) => list.filter(x => x !== id);
const sameError = (state, id) => (state.cartSaveError?.id === id ? null : state.cartSaveError);

export default function reducer(state = initialState, action = {}) {
  const { type, payload } = action;
  switch (type) {
    case FETCH_LISTING_REQUEST:
      return {
        ...state,
        fetchListingInProgress: true,
        currentPageResultIds: [],
        fetchListingRequest: payload,
        fetchListingError: null,
      };
    case FETCH_LISTING_SUCCESS:
      return action.meta?.request !== state.fetchListingRequest
        ? state
        : {
            ...state,
            currentPageResultIds: resultIds(payload.data),
            fetchListingInProgress: false,
          };
    case FETCH_LISTING_ERROR:
      return action.meta?.request !== state.fetchListingRequest
        ? state
        : { ...state, fetchListingInProgress: false, fetchListingError: payload };

    case CART_QUANTITIES_LOADED:
      return { ...state, quantities: payload };
    case CART_QUANTITY_CHANGED:
      return {
        ...state,
        quantities: { ...state.quantities, [payload.id]: payload.quantity },
        cartSaveError: sameError(state, payload.id),
      };
    case CART_QUANTITY_SAVE_ERROR: {
      // Back to the saved quantity, unless the buyer changed it again since
      const { id, quantity, savedQuantity, error } = payload;
      const changedSince = state.quantities[id] !== quantity;
      return {
        ...state,
        quantities: changedSince ? state.quantities : { ...state.quantities, [id]: savedQuantity },
        cartSaveError: { id, error },
      };
    }

    case ADD_ITEM_TO_CART_REQUEST:
      return {
        ...state,
        addItemToCartInProgress: [...state.addItemToCartInProgress, payload],
        cartSaveError: sameError(state, payload),
      };
    case ADD_ITEM_TO_CART_DONE:
      return { ...state, addItemToCartInProgress: without(state.addItemToCartInProgress, payload) };
    case ADD_ITEM_TO_CART_ERROR:
      return {
        ...state,
        addItemToCartInProgress: without(state.addItemToCartInProgress, payload.id),
        cartSaveError: payload,
      };

    case REMOVE_ITEM_FROM_CART_REQUEST:
      return {
        ...state,
        removeItemFromCartInProgress: [...state.removeItemFromCartInProgress, payload],
        cartSaveError: sameError(state, payload),
      };
    case REMOVE_ITEM_FROM_CART_SUCCESS: {
      const { [payload]: _removed, ...quantities } = state.quantities;
      return {
        ...state,
        currentPageResultIds: state.currentPageResultIds.filter(id => id.uuid !== payload),
        quantities,
        removeItemFromCartInProgress: without(state.removeItemFromCartInProgress, payload),
      };
    }
    case REMOVE_ITEM_FROM_CART_ERROR:
      return {
        ...state,
        removeItemFromCartInProgress: without(state.removeItemFromCartInProgress, payload.id),
        cartSaveError: payload,
      };

    case SAVE_FOR_LATER_REQUEST:
      return {
        ...state,
        saveForLaterInProgress: [...state.saveForLaterInProgress, payload],
        cartSaveError: sameError(state, payload),
      };
    case SAVE_FOR_LATER_DONE:
      return { ...state, saveForLaterInProgress: without(state.saveForLaterInProgress, payload) };
    case SAVE_FOR_LATER_ERROR:
      return {
        ...state,
        saveForLaterInProgress: without(state.saveForLaterInProgress, payload.id),
        cartSaveError: payload,
      };

    default:
      return state;
  }
}

// ================ Selectors ================ //

// The cart's listings. The same array until the ids or the entities change, so
// the page doesn't re-render on every store action.
let cartListingsMemo = { entities: null, ids: null, listings: [] };
export const selectCartListings = state => {
  const { entities } = state.marketplaceData;
  const ids = state.CartPage.currentPageResultIds;
  if (entities !== cartListingsMemo.entities || ids !== cartListingsMemo.ids) {
    cartListingsMemo = { entities, ids, listings: getListingsById(state, ids) };
  }
  return cartListingsMemo.listings;
};

const currentUserIdOf = state => state.user?.currentUser?.id?.uuid || null;

// The cart grouped by shop (see cartShopsOf), and its summary. The same objects until
// the listings, the quantities or the signed-in user change.
let cartShopsMemo = { listings: null, quantities: null, userId: null, shops: [], summary: null };
const cartShops = state => {
  const listings = selectCartListings(state);
  const { quantities } = state.CartPage;
  const userId = currentUserIdOf(state);
  const memo = cartShopsMemo;
  if (listings !== memo.listings || quantities !== memo.quantities || userId !== memo.userId) {
    const shops = cartShopsOf(listings, quantities, userId);
    cartShopsMemo = {
      listings,
      quantities,
      userId,
      shops,
      summary: cartSummaryOf(shops, listings, quantities, userId),
    };
  }
  return cartShopsMemo;
};
export const selectCartShops = state => cartShops(state).shops;
export const selectCartSummary = state => cartShops(state).summary;

// ================ Action creators ================ //

const fetchListingRequest = request => ({ type: FETCH_LISTING_REQUEST, payload: request });
export const fetchListingSuccess = (response, request) => ({
  type: FETCH_LISTING_SUCCESS,
  payload: { data: response.data },
  meta: { request },
});
const fetchListingError = (e, request) => ({
  type: FETCH_LISTING_ERROR,
  error: true,
  payload: e,
  meta: { request },
});

const cartQuantitiesLoaded = quantities => ({ type: CART_QUANTITIES_LOADED, payload: quantities });
const cartQuantityChanged = (id, quantity) => ({
  type: CART_QUANTITY_CHANGED,
  payload: { id, quantity },
});
const cartQuantitySaveError = payload => ({
  type: CART_QUANTITY_SAVE_ERROR,
  error: true,
  payload,
});

const addItemToCartRequest = id => ({ type: ADD_ITEM_TO_CART_REQUEST, payload: id });
const addItemToCartDone = id => ({ type: ADD_ITEM_TO_CART_DONE, payload: id });
const addItemToCartError = (id, error) => ({
  type: ADD_ITEM_TO_CART_ERROR,
  error: true,
  payload: { id, error },
});

export const removeItemFromCartRequest = id => ({
  type: REMOVE_ITEM_FROM_CART_REQUEST,
  payload: id,
});
export const removeItemFromCartSuccess = id => ({
  type: REMOVE_ITEM_FROM_CART_SUCCESS,
  payload: id,
});
const removeItemFromCartError = (id, error) => ({
  type: REMOVE_ITEM_FROM_CART_ERROR,
  error: true,
  payload: { id, error },
});

const saveForLaterRequest = id => ({ type: SAVE_FOR_LATER_REQUEST, payload: id });
const saveForLaterDone = id => ({ type: SAVE_FOR_LATER_DONE, payload: id });
const saveForLaterError = (id, error) => ({
  type: SAVE_FOR_LATER_ERROR,
  error: true,
  payload: { id, error },
});

// ================ Thunks ================ //

// The quantities a buyer picked, kept beside the cart's ids so a reload or
// another device shows the same cart. `quantity` null drops the listing.
const withQuantity = (quantities, id, quantity) => {
  const { [id]: _dropped, ...rest } = quantities || {};
  return quantity > 0 ? { ...rest, [id]: quantity } : rest;
};
const withoutQuantities = (quantities, ids) =>
  ids.reduce((rest, id) => withQuantity(rest, id, null), quantities || {});

// The cart as saved: the profile for a signed-in buyer, this browser for a guest.
const savedCart = state =>
  state.auth.isAuthenticated
    ? {
        items: getUserCartDetails(state.user?.currentUser),
        quantities: getUserCartQuantities(state.user?.currentUser),
      }
    : { items: getLocalCartItems(), quantities: getLocalCartQuantities() };

// Saves the cart's ids (when given) and quantities. Rejects when the profile save fails.
const saveCart = ({ items, quantities }) => (dispatch, getState) => {
  if (getState().auth.isAuthenticated) {
    const cartItemsMaybe = items ? { cartItems: items } : {};
    return withBusyCursor(
      dispatch(
        saveCurrentUserProfile({
          protectedData: { ...cartItemsMaybe, cartQuantities: quantities },
        })
      )
    );
  }
  if (items) setLocalCartItems(items);
  setLocalCartQuantities(quantities);
  return Promise.resolve();
};

// Cart changes are saved one at a time, each on top of the cart the last one
// saved, so changes made close together don't overwrite each other.
let cartWrites = Promise.resolve();
const queueCartWrite = write => {
  const run = cartWrites.then(write);
  cartWrites = run.catch(() => {});
  return run;
};

/**
 * "+" and "-" on the cart page. The page shows the new quantity at once; if it
 * can't be saved it goes back and the card says so. Resolves true when saved, false
 * when it wasn't (also when the item has left the saved cart meanwhile).
 */
export const saveCartQuantity = ({ id, quantity }) => (dispatch, getState) => {
  dispatch(cartQuantityChanged(id, quantity));
  return queueCartWrite(() => {
    const { items, quantities } = savedCart(getState());
    if (!items.includes(id)) return false;
    if (quantities[id] === quantity) return true;
    return dispatch(saveCart({ quantities: withQuantity(quantities, id, quantity) }))
      .then(() => {
        // The topbar counts units, so it changes with the quantity
        dispatch(fetchCurrentUserCartCount());
        return true;
      })
      .catch(e => {
        const savedQuantity = savedCart(getState()).quantities[id] || 1;
        dispatch(cartQuantitySaveError({ id, quantity, savedQuantity, error: storableError(e) }));
        return false;
      });
  });
};

/** "Add to cart" on a listing page. Resolves true when saved. */
export const addItemToCart = ({ id, quantity = 1 }) => (dispatch, getState) => {
  dispatch(addItemToCartRequest(id));
  return queueCartWrite(() => {
    const { items, quantities } = savedCart(getState());
    const saved = items.includes(id)
      ? Promise.resolve()
      : dispatch(
          saveCart({ items: [...items, id], quantities: withQuantity(quantities, id, quantity) })
        ).then(() => dispatch(fetchCurrentUserCartCount()));
    return saved
      .then(() => {
        dispatch(addItemToCartDone(id));
        return true;
      })
      .catch(e => {
        dispatch(addItemToCartError(id, storableError(e)));
        return false;
      });
  });
};

/**
 * "Remove" on the cart page and on a listing page. The item leaves the page once
 * the cart is saved; if that fails it stays and the card says so. Resolves true
 * when saved.
 */
export const deleteItemFromCart = ({ id }) => (dispatch, getState) => {
  dispatch(removeItemFromCartRequest(id));
  return queueCartWrite(() => {
    const { items, quantities } = savedCart(getState());
    return dispatch(
      saveCart({ items: without(items, id), quantities: withQuantity(quantities, id, null) })
    )
      .then(() => {
        dispatch(removeItemFromCartSuccess(id));
        dispatch(fetchCurrentUserCartCount());
        return true;
      })
      .catch(e => {
        dispatch(removeItemFromCartError(id, storableError(e)));
        return false;
      });
  });
};

/**
 * "Save for later" on the cart page: the item goes to the buyer's favorites (the same
 * ones the listing page's heart keeps), then leaves the cart. If the favorite can't be
 * saved it stays in the cart and the card says so. Resolves true when both are saved.
 */
export const saveItemForLater = ({ id }) => (dispatch, getState) => {
  dispatch(saveForLaterRequest(id));
  const isFavorite = selectWishlistIds(getState()).includes(id);
  const favorited = isFavorite ? Promise.resolve(true) : dispatch(toggleWishlistItem(id));
  return favorited
    .catch(() => false)
    .then(saved => {
      if (!saved) {
        const error = getState().user?.wishlistSaveError || { message: 'wishlist-save-failed' };
        dispatch(saveForLaterError(id, error));
        return false;
      }
      return dispatch(deleteItemFromCart({ id })).then(removed => {
        dispatch(saveForLaterDone(id));
        return removed;
      });
    });
};

/**
 * Drops listings from the saved cart: ones the listings query no longer returns
 * (closed or deleted), so they don't stay in the cart forever. Resolves true when saved.
 */
export const pruneCartItems = ids => (dispatch, getState) => {
  if (!isArrayLength(ids)) return Promise.resolve(true);
  return queueCartWrite(() => {
    const { items, quantities } = savedCart(getState());
    const gone = ids.filter(id => items.includes(id));
    if (gone.length === 0) return true;
    return dispatch(
      saveCart({
        items: items.filter(id => !gone.includes(id)),
        quantities: withoutQuantities(quantities, gone),
      })
    )
      .then(() => true)
      .catch(() => false);
  });
};

let fetchListingRequests = 0;
export const fetchListings = (ids, config) => (dispatch, getState, sdk) => {
  fetchListingRequests += 1;
  const request = fetchListingRequests;
  dispatch(fetchListingRequest(request));

  const { aspectWidth = 1, aspectHeight = 1, variantPrefix = 'listing-card' } =
    config.layout.listingImage || {};
  const aspectRatio = aspectHeight / aspectWidth;

  const queryParams = {
    include: ['author', 'images', 'currentStock'],
    'fields.listing': ['title', 'geolocation', 'price', 'description', 'publicData', 'state'],
    'fields.user': ['profile.displayName', 'profile.abbreviatedName', 'profile.publicData'],
    ...createImageVariantConfig(`${variantPrefix}`, 400, aspectRatio),
    ...createImageVariantConfig(`${variantPrefix}-2x`, 800, aspectRatio),
    'limit.images': 1,
  };
  // The whole cart, however long, on one page
  return queryListingsByIds(sdk, ids, queryParams)
    .then(response => {
      dispatch(addMarketplaceEntities(response));
      dispatch(fetchListingSuccess(response, request));
      return response;
    })
    .catch(error => {
      dispatch(fetchListingError(storableError(error), request));
      return null;
    });
};

export const loadData = (params, search, config) => async (dispatch, getState) => {
  await dispatch(fetchCurrentUser());
  const { items, quantities } = savedCart(getState());
  dispatch(cartQuantitiesLoaded(quantities));
  // An empty cart (emptied by a checkout, or on another device) shows as empty,
  // not as the listings of the last visit.
  if (isArrayLength(items)) {
    const response = await dispatch(fetchListings(items, config));
    // Listings the query no longer returns are closed or deleted: they leave the saved
    // cart, once, in the browser (the server render doesn't write the profile).
    if (response && typeof window !== 'undefined') {
      const returned = (response.data?.data || []).map(l => l.id?.uuid);
      const gone = items.filter(id => !returned.includes(id));
      if (gone.length > 0) dispatch(pruneCartItems(gone));
    }
    return response;
  }
  fetchListingRequests += 1;
  dispatch(fetchListingRequest(fetchListingRequests));
  dispatch(fetchListingSuccess({ data: { data: [], meta: {} } }, fetchListingRequests));
  return null;
};
